import { isSafari } from 'recording/services/media-stream';

export type AudioPlaybackPipeline = {
  appendChunk: (bytes: Uint8Array, sentenceId: number) => void;
  finishStreaming: () => void;
  playbackDone: Promise<void>;
  cleanup: () => void;
};

const MSE_MIME_TYPE = 'audio/mpeg';

// MSE pipeline: appends each raw chunk directly to a SourceBuffer as it arrives.
// MSE is designed for streaming and handles mid-stream MP3 fragments correctly,
// so no sentence-level grouping is needed — latency is minimised.
// Used for Chrome (and any other non-Safari browser supporting audio/mpeg in MSE)
// — see the isSafari check in createAudioPlaybackPipeline for why Safari never
// takes this path despite supporting MSE.
const createMsePipeline = (
  onAiMediaStream: (stream: MediaStream | null) => void,
  signal: AbortSignal,
): AudioPlaybackPipeline => {
  // AudioContext routes the audio element output to a MediaStreamDestination so
  // the visualizer can receive the stream. captureStream() is not used because
  // Safari does not support it.
  const audioCtx = new AudioContext();
  audioCtx.resume().catch(() => {});
  const streamDestination = audioCtx.createMediaStreamDestination();

  const audioElement = new Audio();
  const mediaSource = new MediaSource();
  const objectUrl = URL.createObjectURL(mediaSource);
  audioElement.src = objectUrl;

  let sourceBuffer: SourceBuffer | null = null;
  const appendQueue: ArrayBuffer[] = [];
  let isDoneStreaming = false;
  let hasStarted = false;
  let resolvePlayback: (() => void) | null = null;

  const playbackDone = new Promise<void>((resolve) => {
    resolvePlayback = resolve;
    signal.addEventListener('abort', () => resolve(), { once: true });
  });

  // Bails out of playback when the stream can't proceed at all (play()
  // rejection, or every chunk failed to append) — without this, playbackDone
  // would await 'ended' forever since nothing ever plays.
  const abortPlayback = () => {
    appendQueue.length = 0;
    onAiMediaStream(null);
    if (resolvePlayback) { resolvePlayback(); resolvePlayback = null; }
  };

  // Full teardown, shared by the public cleanup() and abort — revokeObjectURL
  // and audioCtx.close() are both safe to call more than once, so this can run
  // whether or not the caller also calls cleanup() itself after aborting.
  const cleanup = () => {
    audioElement.pause();
    // Drop the reference to the MediaSource before revoking its object URL —
    // revoking alone doesn't release it if cleanup() runs before sourceopen.
    audioElement.removeAttribute('src');
    URL.revokeObjectURL(objectUrl);
    audioCtx.close().catch(() => {});
    onAiMediaStream(null);
    if (resolvePlayback) { resolvePlayback(); resolvePlayback = null; }
  };

  const tryEndOfStream = () => {
    if (!isDoneStreaming || appendQueue.length > 0) return;
    if (sourceBuffer && sourceBuffer.updating) return;
    if (!hasStarted) {
      // Streaming is finished but nothing was ever successfully appended —
      // 'ended' will never fire for this turn, so resolve directly.
      abortPlayback();
      return;
    }
    if (mediaSource.readyState === 'open') {
      try { mediaSource.endOfStream(); } catch { /* ignore */ }
    }
  };

  const processQueue = () => {
    // A failed chunk is a one-off (bad fragment, transient quota error) —
    // skip it and keep playing the rest rather than stalling the whole turn.
    while (sourceBuffer && !sourceBuffer.updating && appendQueue.length > 0) {
      const chunk = appendQueue.shift()!;
      try {
        sourceBuffer.appendBuffer(chunk);
        return; // append is async — updateend will resume the queue
      } catch { /* drop this chunk and try the next one */ }
    }
  };

  audioElement.addEventListener('ended', () => {
    onAiMediaStream(null);
    if (resolvePlayback) { resolvePlayback(); resolvePlayback = null; }
  });

  signal.addEventListener('abort', cleanup, { once: true });

  mediaSource.addEventListener('sourceopen', () => {
    URL.revokeObjectURL(objectUrl);
    try {
      sourceBuffer = mediaSource.addSourceBuffer(MSE_MIME_TYPE);
    } catch {
      // Can throw despite isTypeSupported() passing (browser quirks / transient
      // MediaSource state) — without sourceBuffer nothing will ever play.
      abortPlayback();
      return;
    }

    // Route audio element through AudioContext so streamDestination captures it
    const sourceNode = audioCtx.createMediaElementSource(audioElement);
    sourceNode.connect(audioCtx.destination);
    sourceNode.connect(streamDestination);

    sourceBuffer.addEventListener('updateend', () => {
      if (!hasStarted) {
        hasStarted = true;
        audioElement.play().catch(() => abortPlayback());
        onAiMediaStream(streamDestination.stream);
      }
      processQueue();
      tryEndOfStream();
    });

    // Drain anything queued before sourceopen fired
    processQueue();
    // Handle the edge case where finishStreaming was called before sourceopen
    tryEndOfStream();
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const appendChunk = (bytes: Uint8Array, _sentenceId: number) => {
    // Slice to own ArrayBuffer; bytes.buffer may be larger than bytes if byteOffset > 0
    appendQueue.push(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength));
    processQueue();
  };

  const finishStreaming = () => {
    isDoneStreaming = true;
    tryEndOfStream();
  };

  return { appendChunk, finishStreaming, playbackDone, cleanup };
};

// Web Audio pipeline: groups chunks by sentence_id before decoding. Individual
// chunks are mid-stream MP3 fragments — not independently decodable. Only the
// full concatenation of all chunks sharing a sentence_id forms a complete MP3
// that AudioContext.decodeAudioData can handle. Used as a fallback when MSE
// does not support audio/mpeg (e.g. Firefox).
const createWebAudioPipeline = (
  onAiMediaStream: (stream: MediaStream | null) => void,
  signal: AbortSignal,
): AudioPlaybackPipeline => {
  const ctx = new AudioContext();
  const streamDestination = ctx.createMediaStreamDestination();
  let nextStartTime = 0;
  let hasNotifiedStream = false;
  let resolvePlayback: (() => void) | null = null;
  let pendingSentences = 0;
  let isDoneStreaming = false;

  const sentenceBuffers = new Map<number, Uint8Array[]>();
  let lastSeenSentenceId: number | null = null;
  let schedulingChain: Promise<void> = Promise.resolve();

  const playbackDone = new Promise<void>((resolve) => {
    resolvePlayback = resolve;
    signal.addEventListener('abort', () => resolve(), { once: true });
  });

  const checkDone = () => {
    if (isDoneStreaming && pendingSentences === 0 && resolvePlayback) {
      onAiMediaStream(null);
      resolvePlayback();
      resolvePlayback = null;
    }
  };

  const decodeSentence = (chunks: Uint8Array[]) => {
    pendingSentences += 1;

    const totalLength = chunks.reduce((sum, c) => sum + c.byteLength, 0);
    const merged = new Uint8Array(totalLength);
    let offset = 0;
    chunks.forEach((c) => { merged.set(c, offset); offset += c.byteLength; });

    // Kick off decode immediately so sentences decode in parallel while the
    // previous one is still playing. schedulingChain then awaits the already-
    // started promise, preserving in-order scheduling without serialising decodes.
    const decodePromise = ctx.decodeAudioData(merged.buffer);

    schedulingChain = schedulingChain.then(async () => {
      if (signal.aborted) { pendingSentences -= 1; checkDone(); return; }
      try {
        const decoded = await decodePromise;
        if (signal.aborted) { pendingSentences -= 1; checkDone(); return; }

        // Ensure AudioContext is running — may be suspended by browser autoplay policy
        await ctx.resume().catch(() => {});

        const source = ctx.createBufferSource();
        source.buffer = decoded;
        source.connect(ctx.destination);
        source.connect(streamDestination);

        const startAt = Math.max(ctx.currentTime, nextStartTime);
        source.start(startAt);
        nextStartTime = startAt + decoded.duration;

        if (!hasNotifiedStream) {
          hasNotifiedStream = true;
          onAiMediaStream(streamDestination.stream);
        }

        source.onended = () => { pendingSentences -= 1; checkDone(); };
      } catch {
        pendingSentences -= 1;
        checkDone();
      }
    });
  };

  const appendChunk = (bytes: Uint8Array, sentenceId: number) => {
    // When sentenceId changes the previous sentence is complete — decode it now
    if (lastSeenSentenceId !== null && lastSeenSentenceId !== sentenceId) {
      const completed = sentenceBuffers.get(lastSeenSentenceId);
      if (completed?.length) decodeSentence(completed);
      sentenceBuffers.delete(lastSeenSentenceId);
    }
    lastSeenSentenceId = sentenceId;
    const bucket = sentenceBuffers.get(sentenceId) ?? [];
    bucket.push(bytes);
    sentenceBuffers.set(sentenceId, bucket);
  };

  const finishStreaming = () => {
    // Decode whatever remains in the last sentence
    if (lastSeenSentenceId !== null) {
      const remaining = sentenceBuffers.get(lastSeenSentenceId);
      if (remaining?.length) decodeSentence(remaining);
      sentenceBuffers.delete(lastSeenSentenceId);
    }
    isDoneStreaming = true;
    checkDone();
  };

  const cleanup = () => {
    ctx.close().catch(() => {});
    sentenceBuffers.clear();
    onAiMediaStream(null);
    if (resolvePlayback) { resolvePlayback(); resolvePlayback = null; }
  };

  return { appendChunk, finishStreaming, playbackDone, cleanup };
};

// Uses MSE (chunk-by-chunk, low latency) when the browser supports audio/mpeg
// in MediaSource; falls back to Web Audio (sentence-by-sentence) otherwise.
// Safari/WebKit is always routed to the Web Audio pipeline even though it supports
// MSE: WebKit has a long-standing bug where no node downstream of a
// MediaElementAudioSourceNode (analyser, script processor, MediaStreamDestination)
// receives real sample data when the source is a streamed <audio> element — audio
// still plays audibly, but the visualizer bubble would always read silence. The
// Web Audio pipeline doesn't use a MediaElementAudioSourceNode at all, so it isn't
// affected.
const createAudioPlaybackPipeline = (
  onAiMediaStream: (stream: MediaStream | null) => void,
  signal: AbortSignal,
): AudioPlaybackPipeline => {
  const useMse = typeof MediaSource !== 'undefined'
    && MediaSource.isTypeSupported(MSE_MIME_TYPE)
    && !isSafari;

  return useMse
    ? createMsePipeline(onAiMediaStream, signal)
    : createWebAudioPipeline(onAiMediaStream, signal);
};

export default createAudioPlaybackPipeline;
